import "@/lib/host/openui-devtools-off";
import { StrictMode } from "react";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";

const FINAL_STATUSES = new Set([400, 401, 403, 404, 409]);
function isFinalError(error: unknown): boolean {
  const status = (error as { status?: unknown })?.status;
  return typeof status === "number" && FINAL_STATUSES.has(status);
}
import type { AnyRoute } from "@tanstack/react-router";
import { rewriteIn, rewriteOut } from "@/lib/host/host-rewrite";
import { NotFound } from "@/components/app/shell/not-found";
import { RouteError } from "@/components/app/shell/route-error";
import { RoutePending } from "@/components/app/shell/route-pending";
import { onMutationError, onQueryError, onQuerySuccess, watchSession } from "@/lib/session/session";

/** Every first path segment the route tree answers to, so `peopleIn` (lib/host/host-rewrite.ts)
 * gives unknown ppl-ai.com addresses the arm's 404. Walked off relative paths: `fullPath` is
 * unset at module load. */
function topLevelSegments(route: AnyRoute, prefix = "", out = new Set<string>()): Set<string> {
  const own = (route.options as { path?: string }).path ?? "";
  const full = `${prefix}/${own}`.replace(/\/+/g, "/").replace(/\/$/, "") || "/";
  const segment = full.split("/")[1] ?? "";
  if (segment !== "" && !segment.startsWith("$")) out.add(segment);
  const children = route.children as AnyRoute[] | Record<string, AnyRoute> | undefined;
  const list = Array.isArray(children) ? children : Object.values(children ?? {});
  for (const child of list) topLevelSegments(child, full === "/" ? "" : full, out);
  return out;
}

const KNOWN_SEGMENTS: ReadonlySet<string> = topLevelSegments(routeTree);

export function getRouter() {
  const queryClient = new QueryClient({
    queryCache: new QueryCache({ onError: onQueryError, onSuccess: onQuerySuccess }),
    mutationCache: new MutationCache({ onError: onMutationError }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // A 401/403/404 is an answer, not a blip: retrying it only delays the
        // redirect (and a paused retry can leave a route pending for good).
        retry: (failureCount, error) => !isFinalError(error) && failureCount < 1,
        networkMode: "always",
        // "always" turns reconnect refetching off; a tablet back on the Wi-Fi should not keep
        // showing what it read before it dropped.
        refetchOnReconnect: true,
      },
      mutations: { networkMode: "always" },
    },
  });
  const router = createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
    /** `jobs.<domain>` is rewritten to the `/jobs-preview/:domain/…` route (also used by staff
     * previews), and the same pair serves the PEOPLE arm (`/v2/…`) from the root of
     * `ppl-ai.com`. */
    rewrite: { input: (args) => rewriteIn(args, KNOWN_SEGMENTS), output: rewriteOut },
    defaultErrorComponent: RouteError,
    defaultPendingComponent: RoutePending,
    defaultNotFoundComponent: NotFound,
    Wrap: ({ children }) => (
      <StrictMode>
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
      </StrictMode>
    ),
  });
  watchSession(queryClient, router);
  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
